import React, { useState, useEffect, useMemo } from "react";
import { Offer } from "@/types/offer";
import { CampLocation } from "@/types/campLocation";
import { SortableOption } from "@/types/fieldsOptions"; 
import { useAuth } from "@/context/AuthContext";
import { OfferFormValues } from "../schemas/offerFormSchema";
import { useOfferForm } from "../hooks/useOfferForm";
import { FieldStateProvider } from "@/services/campDataFieldState";
import { resolveAudience } from "@/services/campDataAudience";
import { OfferFormDialogUI } from "./OfferFormDialogUI";
import { SessionDiffDialog } from "../SessionDiffDialog";
import { useDecimalEnums } from "@/components/admin/locations/hooks/useDecimalEnums";
import { useDiffDialog } from "./hooks/useDiffDialog";
import { useLocationVerification } from "./hooks/useLocationVerification";
import { useOfferDeletion } from "./hooks/useOfferDeletion";
import { prepareSubmissionValues } from "./utils/submissionUtils";
import { toast } from "sonner";

interface OfferFormDialogControllerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (offerData: any) => void | Promise<void>;
  onDelete?: (offerId: string) => void;
  initialDate?: Date;
  location: CampLocation;
  initialOffer?: Offer | null;
  lastOfferByVariation?: Record<string, Offer>;
  availabilityStatusOptions?: SortableOption[]; 
  signupStatusOptions?: SortableOption[];
  isCopyOperation?: boolean;
  savedFormData?: any;
  savedAddAnotherChecked?: boolean;
}

export const OfferFormDialogController: React.FC<OfferFormDialogControllerProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  initialDate,
  location,
  initialOffer,
  lastOfferByVariation = {},
  availabilityStatusOptions = [],
  signupStatusOptions = [],
  isCopyOperation = false,
  savedFormData,
  savedAddAnotherChecked,
}) => {

  const { currentUser, userProfile, userRoles, isAdmin } = useAuth();
  // KB-3318: this offer dialog is reachable from a director route — /organizations/:id/edit (which
  // admits camp_manager) hosts the Sessions tab, which opens this controller. Resolve the badge
  // audience from the viewer's role rather than the provider default ("staff"). Admin → staff.
  const fieldAudience = resolveAudience({ userProfile, userRoles, isAdmin });
  
  // Debug logging for admin status
  
  // Get decimal enums for number of days calculation
  const { enumsMap } = useDecimalEnums();
  
  // Add another checkbox state
  const [addAnotherChecked, setAddAnotherChecked] = useState(savedAddAnotherChecked || false);
  const addAnotherCheckedRef = React.useRef(addAnotherChecked);
  useEffect(() => {
    addAnotherCheckedRef.current = addAnotherChecked;
  }, [addAnotherChecked]);

  // Initialize form
  const {
    form,
    markFieldAsModified,
    resetFieldModified,
    isFieldModified,
    resetFieldToProgram,
    isEqualToProgramValue,
    resetDirtyState,
    isInitialized
  } = useOfferForm({
    initialOffer: savedFormData ? null : initialOffer, // Use null when we have saved data to start fresh
    initialDate,
    lastOfferByVariation,
    location,
    usePublishedData: true,
    savedFormData
  });
  
  // Reset "Add another" state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAddAnotherChecked(false);
    }
  }, [isOpen]);
  
  // Restore checkbox state when reopening with saved state
  useEffect(() => {
    if (isOpen && typeof savedAddAnotherChecked === 'boolean') {
      setAddAnotherChecked(savedAddAnotherChecked);
    }
  }, [isOpen, savedAddAnotherChecked]);
  
  // Use location verification hook
  useLocationVerification({ isOpen, location });
  
  // Use diff dialog hook
  const {
    isDiffDialogOpen,
    setIsDiffDialogOpen,
    diffItems,
    diffData,
    handleShowDiff,
    handleConfirmDiff
  } = useDiffDialog({
    form,
    initialOffer,
    locationId: location.id,
    currentUserId: currentUser?.uid || '',
    isEqualToProgramValue
  });
  
  // Use deletion hook
  const { handleDeleteOffer } = useOfferDeletion({ onDelete, onClose });
  
  // Ensure displayTitle is set from initialOffer if available
  useEffect(() => {
    if (!isOpen || !initialOffer || !initialOffer.displayTitle || !form) return;
    
    const timeoutId = setTimeout(() => {
      const currentDisplayTitle = form.getValues("displayTitle");
      if (!currentDisplayTitle && initialOffer.displayTitle) {
        form.setValue("displayTitle", initialOffer.displayTitle);
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [isOpen, initialOffer, form]);

  const handleSubmit = async (values: OfferFormValues) => {
    
    try {
      const safeValues = prepareSubmissionValues(values, initialOffer, isEqualToProgramValue);
      
      // Validate that we have required fields before calling onSave
      if (!safeValues.variationKey) {
        console.error("Missing required variationKey");
        toast.error("Please select a program before saving");
        return;
      }
      
      if (!safeValues.displayTitle) {
        console.error("Missing required displayTitle");
        toast.error("Please enter a session title before saving");
        return;
      }
      
      const maybePromise: unknown = onSave(safeValues);
      if (maybePromise && typeof (maybePromise as any).then === "function") {
        await (maybePromise as Promise<void>);
      }
      
      // Handle "Add another" functionality for any create flow (new or copy)
      const isCreateFlow = isCopyOperation || !initialOffer?.id;
      if (isCreateFlow && addAnotherCheckedRef.current) {
        // Close modal first
        onClose();
        
        // Dispatch event for parent to reopen modal with saved data
        setTimeout(() => {
          const addAnotherEvent = new CustomEvent('sessionAddAnother', {
            detail: { 
              formData: values,
              addAnotherChecked: true // Preserve the checkbox state
            }
          });
          window.dispatchEvent(addAnotherEvent);
        }, 300);
      }
      // Note: For edit/copy operations, the modal will close normally via the onSave success flow
      
    } catch (error) {
      console.error("=== FORM SUBMISSION ERROR ===");
      console.error("Error preparing submission values:", error);
      console.error("Stack trace:", error instanceof Error ? error.stack : error);
      toast.error("Failed to prepare session data for saving. Please check the console for details.");
    }
  };

  // Shared by both save paths: an invalid form (e.g. a stored legacy status the schema rejects, KB-4101)
  // must say so, never close the diff dialog and save nothing.
  const handleInvalid = (errors: unknown) => {
    console.warn("[OfferFormDialogController] Form validation failed:", errors);
    toast.error("Please fix the highlighted fields before saving.");
  };

  const handleConfirmDiffAndSubmit = () => {
    setIsDiffDialogOpen(false);
    form.handleSubmit(handleSubmit, handleInvalid)();
  };

  // Determine the title based on operation type
  const dialogTitle = isCopyOperation 
    ? "Copy session" 
    : initialOffer?.id 
      ? "Edit session" 
      : "Add new session";


  // Always render the dialog when isOpen is true
  return (
    <>
      <FieldStateProvider kind="offer" id={initialOffer?.id} audience={fieldAudience} currentUid={currentUser?.uid ?? null} enabled={!isCopyOperation && !!initialOffer?.id}>
      <OfferFormDialogUI
        isOpen={isOpen}
        onClose={onClose}
        title={dialogTitle}
        onSave={form.handleSubmit(handleSubmit, handleInvalid)}
        onDelete={handleDeleteOffer}
        form={form}
        initialOffer={initialOffer}
        isAdmin={isAdmin}
        showDiff={handleShowDiff}
        isLoading={!isInitialized}
        availabilityStatusOptions={availabilityStatusOptions}
        signupStatusOptions={signupStatusOptions}
        location={location} 
        markFieldAsModified={markFieldAsModified}
        isFieldModified={isFieldModified}
        resetFieldToProgram={resetFieldToProgram}
        isCopyOperation={isCopyOperation}
        // Show 'Add another like this' for any create flow (new or copy), hide only on edit
        showAddAnother={isCopyOperation || !initialOffer?.id}
        addAnotherChecked={addAnotherChecked}
        onAddAnotherChange={(checked) => {
          addAnotherCheckedRef.current = checked;
          setAddAnotherChecked(checked);
        }}
      />
      </FieldStateProvider>

      {/* Diff Dialog */}
      {diffData && (
        <SessionDiffDialog
          isOpen={isDiffDialogOpen}
          onClose={() => setIsDiffDialogOpen(false)}
          onConfirm={handleConfirmDiffAndSubmit}
          diffItems={diffItems}
          title={initialOffer && !isCopyOperation ? "Session update preview" : "New session preview"}
        />
      )}
    </>
  );
};
